import { defineStore } from 'pinia'
import { getSiteAccessOptions } from '@/services/countrySite'
import type { CenterId, CenterSiteOption } from '@/types/businessCenter'
import type { SiteCountryAccess } from '@/types/countrySite'

let optionsRequest: ReturnType<typeof getSiteAccessOptions> | null = null

export const useSiteContextStore = defineStore('site-context', {
  state: () => ({
    options: [] as CenterSiteOption[],
    countries: [] as SiteCountryAccess[],
    currentSiteId: null as CenterId | null,
    loaded: false,
    loading: false
  }),
  getters: {
    currentSite: state => state.options.find(site => site.siteId === state.currentSiteId) || null,
    currentConcreteSiteId: state => state.currentSiteId
  },
  actions: {
    async fetchOptions(force = false) {
      if (!force && this.loaded) return this.options
      if (!force && optionsRequest) {
        await optionsRequest
        return this.options
      }

      this.loading = true
      optionsRequest = getSiteAccessOptions()
      try {
        const response = await optionsRequest
        this.countries = (response.countries || []).map(country => ({
          ...country,
          countryId: String(country.countryId),
          sites: (country.sites || []).map(site => ({ ...site, siteId: String(site.siteId) }))
        }))
        this.options = this.countries.flatMap(country =>
          country.sites.map(site => ({ siteId: site.siteId, siteName: site.siteName }))
        )
        this.loaded = true
        if (!this.options.some(site => site.siteId === this.currentSiteId)) {
          this.currentSiteId = this.options[0]?.siteId || null
        }
        return this.options
      } finally {
        this.loading = false
        optionsRequest = null
      }
    },
    selectSite(siteId: CenterId) {
      if (this.options.some(site => site.siteId === siteId)) this.currentSiteId = siteId
    }
  },
  persist: {
    pick: ['currentSiteId']
  }
})
